import React, { useMemo } from 'react';
import Plyr from 'plyr-react';
import 'plyr-react/plyr.css';
import type { FormSplitComponent as FormSplitComponentType } from '../schemas/validation';
import { useThemeSafe } from '../contexts/ThemeContext';
import { useCustomColors, useBrandFonts } from '../contexts/CustomColorsContext';
import { getThemeStyles } from '../styles/themes';
import { getFontFamily } from '../utils/fontLoader';
import type { CropData } from './ComponentEditModal/shared/ImageCropModal';
import { getCroppedImageRenderProps } from './ComponentEditModal/shared/cropUtils';
import { FormCore } from './FormCore';
import { EMBED_ASPECT_RATIO_CSS, getEmbedSrc, isEmbedProvider } from '../utils/videoEmbed';

interface FormSplitComponentProps {
  component: FormSplitComponentType;
}

const borderRadiusClasses: Record<string, string> = {
  none: 'rounded-none',
  small: 'rounded-lg',
  medium: 'rounded-xl',
  large: 'rounded-2xl',
  circle: 'rounded-full',
};

const shadowClasses: Record<string, string> = {
  small: 'shadow-sm',
  medium: 'shadow-md',
  large: 'shadow-lg',
};

const FormSplitMedia: React.FC<{
  media: FormSplitComponentType['data']['media'];
  titleFallback: string;
}> = ({ media, titleFallback }) => {
  const getMediaStyles = () => {
    const styles: React.CSSProperties = {};
    if (media.border?.enabled) {
      styles.borderWidth = `${media.border.width}px`;
      styles.borderColor = media.border.color;
      styles.borderStyle = media.border.style;
    }
    return styles;
  };

  const getMediaClasses = (includeAspectRatio = true) => {
    let classes = '';
    const borderRadiusKey =
      media.type === 'video' && media.borderRadius === 'circle' ? 'large' : media.borderRadius;
    if (borderRadiusKey) {
      classes += ` ${borderRadiusClasses[borderRadiusKey]}`;
    }
    if (media.shadow?.enabled && media.shadow.size) {
      classes += ` ${shadowClasses[media.shadow.size]}`;
    }
    if (includeAspectRatio && media.aspectRatio && media.aspectRatio !== 'auto') {
      const aspectRatioClasses: Record<string, string> = {
        '16:9': 'aspect-video',
        '4:3': 'aspect-[4/3]',
        '1:1': 'aspect-square',
        '3:2': 'aspect-[3/2]',
        '9:16': 'aspect-[9/16]',
      };
      classes += ` ${aspectRatioClasses[media.aspectRatio] || ''}`;
    }
    return classes.trim();
  };

  const videoSourceType = (media as { sourceType?: string }).sourceType || 'upload';
  const videoIsEmbed = isEmbedProvider(videoSourceType);
  const videoUrl = media.cloudfrontUrl || media.url;
  const videoPoster =
    (media as { thumbnail?: { cloudfrontUrl?: string; url?: string } }).thumbnail?.cloudfrontUrl ||
    (media as { thumbnail?: { url?: string } }).thumbnail?.url ||
    media.poster;
  const memoizedVideoSrc = useMemo(() => {
    if (media.type !== 'video' || videoIsEmbed) return null;
    return {
      type: 'video' as const,
      sources: [{ src: videoUrl, type: 'video/mp4' }],
      poster: videoPoster,
    };
  }, [media.type, videoIsEmbed, videoUrl, videoPoster]);

  const memoizedPlyrOptions = useMemo(
    () => ({
      controls:
        media.controls !== false
          ? ['play-large', 'play', 'progress', 'current-time', 'mute', 'volume', 'fullscreen']
          : [],
      autoplay: media.autoplay || false,
      muted: media.muted !== false,
      loop: { active: media.loop || false },
      hideControls: !media.controls,
      clickToPlay: true,
      keyboard: { focused: true, global: false },
      tooltips: { controls: true, seek: true },
      storage: { enabled: false },
    }),
    [media.controls, media.autoplay, media.muted, media.loop]
  );

  const mediaStyles = getMediaStyles();

  if (media.type === 'video') {
    const mediaClasses = getMediaClasses(false);

    if (videoIsEmbed && media.url) {
      const embedSrc = isEmbedProvider(videoSourceType)
        ? getEmbedSrc(videoSourceType, media.url)
        : null;

      return (
        <div className={`w-full ${mediaClasses} overflow-hidden`} style={mediaStyles}>
          <div
            className="w-full"
            style={{ aspectRatio: EMBED_ASPECT_RATIO_CSS, background: '#000' }}
          >
            <iframe
              src={embedSrc || ''}
              className="w-full h-full"
              style={{ border: 0 }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title="Video"
            />
          </div>
        </div>
      );
    }

    if (memoizedVideoSrc) {
      return (
        <div className={`w-full ${mediaClasses} overflow-hidden`} style={mediaStyles}>
          <div className="w-full">
            <Plyr source={memoizedVideoSrc} options={memoizedPlyrOptions} />
          </div>
        </div>
      );
    }
  }

  const mediaClasses = getMediaClasses();
  const crop = media.crop as CropData | undefined;
  const { hasCrop, aspectRatio, imageStyle } = getCroppedImageRenderProps(crop);

  if (hasCrop) {
    return (
      <div
        className={`${mediaClasses} relative overflow-hidden`}
        style={{
          ...mediaStyles,
          aspectRatio: aspectRatio,
          width: '100%',
        }}
      >
        <img
          src={media.cloudfrontUrl || media.url}
          alt={media.alt || titleFallback}
          style={imageStyle}
        />
      </div>
    );
  }

  const baseImageStyle = (crop as { grayscale?: boolean } | undefined)?.grayscale
    ? { filter: 'grayscale(100%)' }
    : {};

  return (
    <div className={`${mediaClasses} overflow-hidden`}>
      <img
        src={media.cloudfrontUrl || media.url}
        alt={media.alt || titleFallback}
        className="w-full h-full object-cover"
        style={{ ...mediaStyles, ...baseImageStyle }}
        width={media.width}
        height={media.height}
      />
    </div>
  );
};

export const FormSplitComponent: React.FC<FormSplitComponentProps> = ({ component }) => {
  const { currentTheme } = useThemeSafe();
  const customColors = useCustomColors();
  const themeStyles = getThemeStyles(currentTheme, customColors || undefined);
  const brandFonts = useBrandFonts();

  const headingFontStyle = brandFonts?.headingFont
    ? { fontFamily: getFontFamily(brandFonts.headingFont) }
    : {};
  const bodyFontStyle = brandFonts?.bodyFont
    ? { fontFamily: getFontFamily(brandFonts.bodyFont) }
    : {};

  const raw = component.data;
  const data = {
    ...raw,
    media: {
      ...raw.media,
      type: raw.media?.type || 'image',
      url: raw.media?.url || '',
    },
    mediaPosition: raw.mediaPosition ?? 'left',
    stackOnMobile: raw.stackOnMobile !== false,
  };

  const bgColor =
    (data as any).customColors?.background ||
    data.styling?.backgroundColor ||
    themeStyles.form?.background ||
    '#ffffff';
  const textColor =
    (data as any).customColors?.text ||
    data.styling?.textColor ||
    themeStyles.form?.labelColor ||
    '#374151';
  const labelColor = (data as any).customColors?.label || textColor;
  const titleColor = (data as any).customColors?.title || labelColor;

  const alignmentClasses: Record<string, string> = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };

  const alignment = data.styling.alignment || 'center';

  const stackOnMobile = data.stackOnMobile !== false;
  const mediaPosition = data.mediaPosition || 'left';

  const mediaOrderMobile = stackOnMobile ? 'order-1' : 'order-2';
  const formOrderMobile = stackOnMobile ? 'order-2' : 'order-1';
  const mediaOrderMd = mediaPosition === 'left' ? 'md:order-1' : 'md:order-2';
  const formOrderMd = mediaPosition === 'left' ? 'md:order-2' : 'md:order-1';

  const hasMediaUrl = Boolean(data.media?.url || data.media?.cloudfrontUrl);

  return (
    <section className="py-16 md:py-24" style={{ backgroundColor: bgColor }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12">
          <div
            className={`max-w-4xl ${alignment === 'center' ? 'mx-auto' : alignment === 'right' ? 'ml-auto' : ''}`}
          >
            <h2
              className={`text-3xl md:text-4xl font-bold mb-4 ${alignmentClasses[alignment]}`}
              style={{ color: titleColor, ...headingFontStyle }}
            >
              {data.title}
            </h2>
            {data.description && (
              <p
                className={`text-lg max-w-2xl ${alignmentClasses[alignment]} ${alignment === 'center' ? 'mx-auto' : ''} opacity-80`}
                style={{ color: labelColor, ...bodyFontStyle }}
              >
                {data.description}
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 items-start">
          <div className={`${mediaOrderMobile} ${mediaOrderMd}`}>
            {hasMediaUrl ? (
              <FormSplitMedia media={data.media} titleFallback={data.title} />
            ) : (
              <div
                className="w-full aspect-video rounded-xl bg-black/5 flex items-center justify-center text-sm opacity-50"
                style={{ color: labelColor }}
              >
                Add media in the editor
              </div>
            )}
          </div>
          <div className={`${formOrderMobile} ${formOrderMd} w-full min-w-0`}>
            <FormCore component={component} className="w-full" />
          </div>
        </div>
      </div>
    </section>
  );
};
